import "./attendanceStyle.css";
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { MdClear } from "react-icons/md";
import { RiFileExcel2Line } from "react-icons/ri";
import { FaFileCsv, FaFilePdf, FaDownload, FaChevronUp } from "react-icons/fa";
import { useGlobalToast } from '../../stopOldToastGlobally.js';
import {
  getInitials,
  getInitialColor,
  formatHeader,
} from "../../services/commonServices";
import CustomPagination from "../../components/CustomPagination";
import TableSkeleton from "../../components/table/TableSkeleton.js";
import { fetchBranchData, initBranchState } from "../../redux/slices/branchSlice";
import { fetchDepartmentData, initDepartState } from "../../redux/slices/departmentSlice";
import { fetchData, initShiftState } from "../../redux/slices/shiftsSlice";
import { fetchLiveAttData, initLiveAttState } from "../../redux/slices/liveAttendanceSlice";
import { useDispatch, useSelector } from "react-redux";
import { liveAttendancePath } from "../../services/config";
import AttendanceSummary from "../../components/InfoCard/attendanceInfoCard";

/* ═══════════════════════════════════════════════════
   CONSTANTSd
═══════════════════════════════════════════════════ */
const STATUS_STYLE_MAP = {
  "0":  { bg: "#6B7280", color: "#FFFFFF" },
  "1":  { bg: "#16A34A", color: "#FFFFFF" },
  "2":  { bg: "#2563EB", color: "#FFFFFF" },
  "3":  { bg: "#D97706", color: "#FFFFFF" },
  "4":  { bg: "#DC2626", color: "#FFFFFF" },
  "5":  { bg: "#7C3AED", color: "#FFFFFF" },
  "6":  { bg: "#DC2626", color: "#FFFFFF" },
  "7":  { bg: "#D97706", color: "#FFFFFF" },
  "8":  { bg: "#0891B2", color: "#FFFFFF" },
  "9":  { bg: "#16A34A", color: "#FFFFFF" },
  "10": { bg: "#D97706", color: "#FFFFFF" },
  "11": { bg: "#6B7280", color: "#FFFFFF" },
};

const resolveStatusStyle = (statusCode, statusText) => {
  const fromCode = STATUS_STYLE_MAP[String(statusCode)];
  if (fromCode) return fromCode;
  const s = (statusText || "").toLowerCase();
  if (s.includes("punch in")  || s.includes("present"))   return STATUS_STYLE_MAP["1"];
  if (s.includes("punch out"))                             return STATUS_STYLE_MAP["2"];
  if (s.includes("early"))                                 return STATUS_STYLE_MAP["3"];
  if (s.includes("late"))                                  return STATUS_STYLE_MAP["4"];
  if (s.includes("absent")    || s.includes("no punch"))  return STATUS_STYLE_MAP["6"];
  if (s.includes("no shift"))                              return STATUS_STYLE_MAP["5"];
  if (s.includes("week off")  || s.includes("weekoff"))   return STATUS_STYLE_MAP["7"];
  if (s.includes("holiday"))                               return STATUS_STYLE_MAP["8"];
  if (s.includes("half day")  || s.includes("halfday"))   return STATUS_STYLE_MAP["10"];
  if (s.includes("no att"))                                return STATUS_STYLE_MAP["11"];
  return STATUS_STYLE_MAP["0"];
};

const buildColumnDefs = (handleNavigate, showImage) => [
  {
    headerName: "S.No",
    width: 70,
    pinned: "left",
    headerClass: "ag-header-custom text-center",
    cellClass: "text-center",
    cellRenderer: (params) => {
      const { node, api } = params;
      const currentPage = (api && api.paginationGetCurrentPage) ? api.paginationGetCurrentPage() : 0;
      const pageSize = (api && api.paginationGetPageSize) ? api.paginationGetPageSize() : (params?.context?.pageSize || 10);
      const serial = currentPage * (pageSize || 10) + (node?.rowIndex ?? 0) + 1;
      return (
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{serial}</div>
      );
    },
  },
  {
    headerName:  "Employee ID",
    field:       "employeeId",
    width:       130,
    pinned:      "left",
    headerClass: "ag-header-custom",
    cellRenderer: ({ data }) => (
      <span style={{ fontSize: "12px", fontWeight: "500", fontFamily: "monospace", color: "var(--cell-muted)" }}>
        {data.employeeId || "-"}
      </span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName:  "Staff Name",
    field:       "staffName",
    width:       220,
    pinned:      "left",
    headerClass: "ag-header-custom",
    cellRenderer: ({ data }) => (
      <div style={{ display: "flex", alignItems: "center", gap: "10px", height: "100%", width: "100%" }}>
        <div style={{
          width: "32px", height: "32px", borderRadius: "50%", overflow: "hidden",
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: getInitialColor(data.staffName || ""),
          color: "#FFF", fontWeight: "600", fontSize: "12px", flexShrink: 0,
        }}>
          {getInitials(data.staffName || "")}
        </div>
        <span style={{ fontSize: "13px", fontWeight: "500", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--cell-primary)" }}>
          {data.staffName || "-"}
        </span>
      </div>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Designation", field: "designation", width: 155, headerClass: "ag-header-custom",
    cellRenderer: ({ data }) => (
      <span style={{ fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--cell-primary)" }}>{data.designation || "-"}</span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Branch", field: "branchName", width: 160, headerClass: "ag-header-custom",
    cellRenderer: ({ data }) => (
      <span style={{ fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--cell-primary)" }}>{data.branchName || "-"}</span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Check In Image", field: "clockInImage", width: 130, headerClass: "ag-header-custom",
    cellRenderer: ({ data }) =>
      data.clockInImage ? (
        <div
          style={{ backgroundColor: getInitialColor(data.staffName || ""), width: "36px", height: "36px", borderRadius: "50%", overflow: "hidden", cursor: "pointer" }}
          onClick={() => showImage(data.clockInImage)}
        >
          <img src={data.clockInImage} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="check-in" />
        </div>
      ) : (
        <span style={{ fontSize: "13px", color: "var(--cell-muted)" }}>-</span>
      ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Check In Location", field: "checkInLocation", width: 160, headerClass: "ag-header-custom",
    cellRenderer: ({ data }) => (
      <span style={{ fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--cell-primary)" }}>{data.checkInLocation || "-"}</span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "First In", field: "punchIn", width: 100, headerClass: "ag-header-custom",
    cellRenderer: ({ data }) => (
      <span style={{ fontSize: "13px", color: "var(--cell-primary)" }}>{data.punchIn || "-"}</span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Check Out Image", field: "clockOutImage", width: 140, headerClass: "ag-header-custom",
    cellRenderer: ({ data }) =>
      data.clockOutImage ? (
        <div
          style={{ backgroundColor: getInitialColor(data.staffName || ""), width: "36px", height: "36px", borderRadius: "50%", overflow: "hidden", cursor: "pointer" }}
          onClick={() => showImage(data.clockOutImage)}
        >
          <img src={data.clockOutImage} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="check-out" />
        </div>
      ) : (
        <span style={{ fontSize: "13px", color: "var(--cell-muted)" }}>-</span>
      ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Check Out Location", field: "checkOutLocation", width: 160, headerClass: "ag-header-custom",
    cellRenderer: ({ data }) => (
      <span style={{ fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--cell-primary)" }}>{data.checkOutLocation || "-"}</span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Last Out", field: "punchOut", width: 100, headerClass: "ag-header-custom",
    cellRenderer: ({ data }) => (
      <span style={{ fontSize: "13px", color: "var(--cell-primary)" }}>{data.punchOut || "-"}</span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Status", field: "status", width: 150, pinned: "right", headerClass: "ag-header-custom",
    cellRenderer: ({ data }) => {
      const style = resolveStatusStyle(data.statusCode, data.status);
      return (
        <div
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            height: "30px", borderRadius: "6px", padding: "0 14px",
            fontSize: "12px", fontWeight: "600", cursor: "pointer",
            minWidth: "90px", maxWidth: "130px", whiteSpace: "nowrap",
            backgroundColor: style.bg, color: style.color,
            border: "none", letterSpacing: "0.3px",
            boxShadow: `0 2px 6px ${style.bg}55`,
          }}
          onClick={() => handleNavigate(data.status)}
        >
          {formatHeader(data.status) || "-"}
        </div>
      );
    },
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
  },
];

const getThemeColors = () => {
  const isDark = document.documentElement.getAttribute("data-theme") === "dark";
  return {
    isDark,
    bgDropdown:      isDark ? "#1E1A30" : "#FFFFFF",
    bgDropdownHover: isDark ? "#2D2845" : "#F3F4F6",
    borderDefault:   isDark ? "#2D2A45" : "#E0E0F0",
    divider:         isDark ? "#2D2A45" : "#F0F0F0",
    dropdownText:    isDark ? "#E3E2EB" : "#333333",
  };
};

/* ═══════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════ */
const LiveAttendance = () => {
  const navigate    = useNavigate();
  const customToast = useGlobalToast(); 
  const dispatch    = useDispatch();
  const gridRef     = useRef(null);
  const dropdownRef = useRef(null);

  // Keep a ref to current filters so the polling interval always reads fresh values
  // without needing to be recreated every time filters change.
  const filtersRef = useRef({ branchId: "", shiftId: "", departId: "" });

  const user = useMemo(() => JSON.parse(localStorage.getItem("user_data")) || {}, []);

  const branchState         = useSelector((s) => s.branch);
  const departState         = useSelector((s) => s.department);
  const shiftState          = useSelector((s) => s.shift);
  const liveAttendanceState = useSelector((s) => s.liveAttendance);

  const [gridApi,           setGridApi]           = useState(null);
  const [showExportMenu,    setShowExportMenu]     = useState(false);
  const [branch,            setBranch]             = useState([]);
  const [shift,             setShift]              = useState([]);
  const [departmentList,    setDepartmentList]     = useState([]);

  // ── The three "card" values — kept in a single object so one setState
  //    always updates all three atomically (no half-rendered state).
  const [summaryData, setSummaryData] = useState({
    attendanceSummary: {},
    totalStaffCount:   "",
  });

  const [staffList,         setStaffList]         = useState([]);
  const [rawList,           setRawList]           = useState([]); // unfiltered master list
  const [searchQuery,       setSearchQuery]       = useState("");
  const [themeColors,       setThemeColors]       = useState(getThemeColors());
  const [filters,           setFilters]           = useState({ branchId: "", shiftId: "", departId: "" });

  /* Keep filtersRef in sync so polling closure always reads latest values */
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  /* Theme observer */
  useEffect(() => {
    const observer = new MutationObserver(() => setThemeColors(getThemeColors()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  /* Close export menu on outside click */
  useEffect(() => {
    const h = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowExportMenu(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const onGridReady = (params) => {
    setGridApi(params.api);
    gridRef.current = params.api;
  };

  /* Fetch dropdown data once */
  const loadDropdowns = useCallback(() => {
    dispatch(fetchBranchData(user.user_id));
    dispatch(fetchDepartmentData(user.user_id));
    dispatch(fetchData(user.user_id));
  }, [dispatch, user.user_id]);

  useEffect(() => { loadDropdowns(); }, [loadDropdowns]);

  /* Handle dropdown slice responses */
  useEffect(() => {
    if (branchState.brnGetSuccess)  { setBranch(branchState.brnGetData);        dispatch(initBranchState()); }
    if (departState.depGetSuccess)  { setDepartmentList(departState.depGetData); dispatch(initDepartState()); }
    if (shiftState.shiftGetSuccess) { setShift(shiftState.shiftGetData);         dispatch(initShiftState());  }
  }, [branchState.brnGetSuccess, departState.depGetSuccess, shiftState.shiftGetSuccess]); // eslint-disable-line

  /* Core fetch — reads from filtersRef so it's stable and never stale */
  const fetchLive = useCallback(() => {
    const { branchId, shiftId, departId } = filtersRef.current;
    dispatch(fetchLiveAttData({
      path: liveAttendancePath,
      data: { userId: user.user_id, branchId, shiftId, departmentId: departId },
    }));
  }, [dispatch, user.user_id]);

  /* Initial fetch + polling every 100 s */
  useEffect(() => {
    fetchLive();
    const interval = setInterval(fetchLive, 100_000);
    return () => clearInterval(interval);
  }, [fetchLive]);

  /* ── KEY FIX: handle live attendance response ──
     Always update summaryData + rawList atomically.
     Apply current search filter on top so cards AND table stay in sync. */
  useEffect(() => {
    if (liveAttendanceState.liveAttGetSuccess) {
      const d = liveAttendanceState.liveAttGetData?.data || {};
      const liveData       = d.liveAttendanceData   || [];
      const totalCount     = d.totalStaffCount      ?? "";
      const summary        = d.attendanceSummary    || {};

      // Update cards ALWAYS (even when a search is active)
      setSummaryData({ attendanceSummary: summary, totalStaffCount: totalCount });

      // Store raw unfiltered list
      setRawList(liveData);

      // Apply active search on freshly received data
      setStaffList(
        searchQuery
          ? liveData.filter(
              (v) =>
                v.staffName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                v.employeeId?.toLowerCase().includes(searchQuery.toLowerCase())
            )
          : liveData
      );

      // Re-fetch dropdowns to keep selects fresh
      loadDropdowns();
      dispatch(initLiveAttState());
    }

    if (liveAttendanceState.liveAttGetError) {
      setSummaryData({ attendanceSummary: {}, totalStaffCount: "" });
      setRawList([]);
      setStaffList([]);
      dispatch(initLiveAttState());
    }
  }, [liveAttendanceState]); // eslint-disable-line

  /* ── Filter handlers ── */
  const handleFilterChange = ({ target: { name, value } }) => {
    const next = { ...filtersRef.current, [name]: value };
    filtersRef.current = next; // update ref immediately so fetchLive picks it up
    setFilters(next);
    dispatch(fetchLiveAttData({
      path: liveAttendancePath,
      data: { userId: user.user_id, branchId: next.branchId, shiftId: next.shiftId, departmentId: next.departId },
    }));
  };

  const handleSearch = (val) => {
    setSearchQuery(val);
    setStaffList(
      val
        ? rawList.filter(
            (v) =>
              v.staffName?.toLowerCase().includes(val.toLowerCase()) ||
              v.employeeId?.toLowerCase().includes(val.toLowerCase())
          )
        : rawList
    );
  };

  const clearFilter = () => {
    const reset = { branchId: "", shiftId: "", departId: "" };
    filtersRef.current = reset;
    setFilters(reset);
    setSearchQuery("");
    dispatch(fetchLiveAttData({
      path: liveAttendancePath,
      data: { userId: user.user_id, branchId: "", shiftId: "", departmentId: "" },
    }));
  };

  /* Navigation */
  const handleNavigate = useCallback((status) => {
    if (status === "No AttendanceSettings") navigate("/attendance-setting");
  }, [navigate]);

  const showImage = useCallback((url) => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url; a.target = "_blank"; a.click();
  }, []);

  const columnDefs = useMemo(
    () => buildColumnDefs(handleNavigate, showImage),
    [handleNavigate, showImage]
  );

  /* ── Export helpers ── */
  const exportCols    = ["employeeId","staffName","designation","branchName","checkInLocation","punchIn","checkOutLocation","punchOut","status"];
  const exportHeaders = ["Employee ID","Staff Name","Designation","Branch","Check In Location","First In","Check Out Location","Last Out","Status"];

  const exportCSV = () => {
    setShowExportMenu(false);
   
  if (!staffList || staffList.length === 0) {
    customToast.warning("No data to export");  // ✅
    return;
  }

    const rows = [exportHeaders.join(","), ...staffList.map((r) => exportCols.map((c) => `"${r[c] ?? ""}"`).join(","))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    Object.assign(document.createElement("a"), { href: url, download: `Live_Attendance_${new Date().toLocaleDateString()}.csv` }).click();
    URL.revokeObjectURL(url);
    customToast.success("CSV downloaded successfully!");
  };

  const exportExcel = async () => {
    setShowExportMenu(false);
     if (!staffList || staffList.length === 0) {
    customToast.warning("No data to export");  // ✅
    return;
  }
    try {
      const XLSX = await import("https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs");
      const ws   = XLSX.utils.aoa_to_sheet([exportHeaders, ...staffList.map((r) => exportCols.map((c) => r[c] ?? ""))]);
      const wb   = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Live Attendance");
      XLSX.writeFile(wb, `Live_Attendance_${new Date().toLocaleDateString()}.xlsx`);
    } catch {
      const rows = [exportHeaders.join("\t"), ...staffList.map((r) => exportCols.map((c) => r[c] ?? "").join("\t"))];
      const blob = new Blob([rows.join("\n")], { type: "application/vnd.ms-excel" });
      const url  = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), { href: url, download: `Live_Attendance_${new Date().toLocaleDateString()}.xls` }).click();
      URL.revokeObjectURL(url);
      customToast.success("Excel downloaded successfully!");
    }
  };

  const exportPDF = () => {
    setShowExportMenu(false);
      if (!staffList || staffList.length === 0) {
    customToast.warning("No data to export");  // ✅
    return;
  }
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Live Attendance</title>
        <style>
          body{font-family:Arial,sans-serif;font-size:11px;margin:20px}
          h2{color:#333;margin-bottom:10px}
          table{width:100%;border-collapse:collapse}
          th{background:#8660F8;color:white;padding:8px 6px;text-align:left;font-size:11px}
          td{padding:6px;border-bottom:1px solid #eee;font-size:11px}
          tr:nth-child(even){background:#f9f9f9}
          .footer{margin-top:15px;font-size:10px;color:gray}
        </style>
      </head><body>
        <h2>Live Attendance Report — ${new Date().toLocaleDateString()}</h2>
        <table>
          <thead><tr>${exportHeaders.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
          <tbody>${staffList.map((r) => `<tr>${exportCols.map((c) => `<td>${r[c] ?? "-"}</td>`).join("")}</tr>`).join("")}</tbody>
        </table>
        <div class="footer">Generated on ${new Date().toLocaleString()}</div>
      </body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
      customToast.success("PDF exported successfully!"); 
  };

  const hasActiveFilter = searchQuery || filters.shiftId || filters.departId || filters.branchId;

  /* ── Dropdown style helper ── */
  const dropdownItemStyle = {
    display: "flex", alignItems: "center", gap: "10px",
    width: "100%", padding: "11px 16px", border: "none",
    backgroundColor: themeColors.bgDropdown, cursor: "pointer",
    fontSize: "13px", fontWeight: "500", color: themeColors.dropdownText,
    textAlign: "left", transition: "background 0.15s",
  };

  /* ══════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════ */
  return (
    <div
      className="live-attendance-container"
      style={{
        display: "flex", flexDirection: "column",
        height: "calc(100vh - 80px)",
        overflow: "hidden", maxWidth: "100%",
      }}
    >
      {/* ── Summary Cards ──
          summaryData is now always updated from the latest API response,
          regardless of search state. */}
      <div className="d-flex align-items-center justify-content-between mt-2" style={{ flexShrink: 0 }}>
        <AttendanceSummary
          color="green-bg-1"    label="Present Employees"
          value={summaryData.attendanceSummary?.PunchIn        ?? 0}
          total={summaryData.totalStaffCount}
        />
        <AttendanceSummary
          color="red-bg-1"      label="Absent Employees"
          value={summaryData.attendanceSummary?.Absent         ?? 0}
          total={summaryData.totalStaffCount}
        />
        <AttendanceSummary
          color="lavender-bg-1" label="Half Day"
          value={summaryData.attendanceSummary?.HalfDay        ?? 0}
          total={summaryData.totalStaffCount}
        />
        <AttendanceSummary
          color="blue-bg-1"     label="Late Clock In"
          value={summaryData.attendanceSummary?.["Late Checkin"]    ?? 0}
          total={summaryData.totalStaffCount}
        />
        <AttendanceSummary
          color="orange-bg-1"   label="Early Clock Out"
          value={summaryData.attendanceSummary?.["Early Checkout"]  ?? 0}
          total={summaryData.totalStaffCount}
        />
      </div>

      {/* ── Filter Row ── */}
      <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2" style={{ flexShrink: 0 }}>
        <Form className="d-flex align-items-center justify-content-start gap-3 flex-wrap">
          <Form.Control
            placeholder="Search by Employee Name & ID"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="universal-input-search"
            style={{ width: "250px" }}
          />
          <Form.Select name="branchId" value={filters.branchId} onChange={handleFilterChange} className="universal-input-one" style={{ width: "200px" }}>
            <option value="">Select Branches</option>
            {branch.map((r, i) => <option value={r.branchId} key={i}>{r.branch_name}</option>)}
          </Form.Select>
          <Form.Select name="shiftId" value={filters.shiftId} onChange={handleFilterChange} className="universal-input-one" style={{ width: "200px" }}>
            <option value="">Select Shifts</option>
            {shift.map((r, i) => <option value={r.shiftId} key={i}>{r.shiftName}</option>)}
          </Form.Select>
          <Form.Select name="departId" value={filters.departId} onChange={handleFilterChange} className="universal-input-one" style={{ width: "200px" }}>
            <option value="">Select Department</option>
            {departmentList.map((r, i) => <option value={r.departmentId} key={i}>{r.department_name}</option>)}
          </Form.Select>
          {hasActiveFilter && (
            <Form.Group>
              <button type="button" className="clear" onClick={clearFilter}><MdClear /> Clear</button>
            </Form.Group>
          )}
        </Form>
      </div>

      {/* ── AG Grid ── */}
      <div
        className="ag-theme-quartz custom-grid mt-3"
        style={{ flex: "1 1 0", minHeight: 0, width: "100%", overflow: "hidden", boxSizing: "border-box" }}
      >
        {liveAttendanceState.liveAttGetLoading ? (
          <TableSkeleton />
        ) : (
          <AgGridReact
            ref={gridRef}
            rowData={Array.isArray(staffList) ? staffList : []}
            columnDefs={columnDefs}
            rowHeight={52}
            headerHeight={44}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[10, 20, 50, 100]}
            onGridReady={onGridReady}
            domLayout="normal"
            suppressHorizontalScroll={false}
            suppressCellFocus={true}
            animateRows={true}
            enableCellTextSelection={true}
            ensureDomOrder={true}
            defaultColDef={{
              cellStyle: { display: "flex", alignItems: "center" },
              resizable: true, sortable: true, minWidth: 80,
              wrapHeaderText: false, autoHeaderHeight: false,
            }}
          />
        )}
      </div>

      {/* ── Pagination + Export ── */}
      <div
        className="d-flex align-items-center justify-content-between custom-pagination"
        style={{
          flexShrink: 0, paddingTop: "8px", paddingBottom: "8px",
          background: "var(--bg-page, inherit)",
          borderTop: "1px solid var(--border, #e5e7eb)",
          position: "relative", zIndex: 10,
        }}
      >
        {gridApi && <CustomPagination gridApi={gridApi} />}

        <div ref={dropdownRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setShowExportMenu((p) => !p)}
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "10px 24px", borderRadius: "8px", border: "none",
              cursor: "pointer", fontSize: "13px", fontWeight: "600",
              backgroundColor: "#7C3AED", color: "#fff",
              boxShadow: "0 2px 8px rgba(124,58,237,0.25)", transition: "background 0.2s",
            }}
          >
            <FaDownload size={13} />
            Download Report
            <FaChevronUp size={12} style={{ transform: showExportMenu ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.2s" }} />
          </button>

          {showExportMenu && (
            <div style={{
              position: "absolute", bottom: "110%", right: 0,
              backgroundColor: themeColors.bgDropdown,
              borderRadius: "10px",
              boxShadow: themeColors.isDark ? "0 4px 20px rgba(0,0,0,0.5)" : "0 4px 20px rgba(0,0,0,0.15)",
              border: `1px solid ${themeColors.borderDefault}`,
              overflow: "hidden", minWidth: "185px", zIndex: 1000,
            }}>
              <button type="button" onClick={exportExcel} style={dropdownItemStyle}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = themeColors.bgDropdownHover)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = themeColors.bgDropdown)}>
                <RiFileExcel2Line size={16} color="#1D6F42" /> Export as Excel
              </button>
              <div style={{ height: "1px", backgroundColor: themeColors.divider }} />
              <button type="button" onClick={exportCSV} style={dropdownItemStyle}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = themeColors.bgDropdownHover)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = themeColors.bgDropdown)}>
                <FaFileCsv size={16} color="#0070C0" /> Export as CSV
              </button>
              <div style={{ height: "1px", backgroundColor: themeColors.divider }} />
              <button type="button" onClick={exportPDF} style={dropdownItemStyle}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = themeColors.bgDropdownHover)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = themeColors.bgDropdown)}>
                <FaFilePdf size={16} color="#E53935" /> Export as PDF
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveAttendance;